import axios, { type AxiosInstance } from 'axios';
import type {
  AlignmentSnapshot,
  CoachingAccessKey,
  CoachingActionCreate,
  CoachingActionUpdate,
  CoachingAffirmationUpdate,
  CoachingEveningReview,
  CoachingEventName,
  CoachingFeeling,
  CoachingAuraCheckIn,
  CoachingFutureSelfSave,
  FutureSelfHorizon,
  FutureSelfPromptKey,
  CoachingGoalCreate,
  CoachingGoalUpdate,
  CoachingJournalCreate,
  CoachingMilestoneCreate,
  CoachingMilestoneUpdate,
  CoachingMorningRitual,
  CoachingPractice,
  CoachingProfilePatch,
  CoachingThreeSixNineSave,
  CoachingThreeSixNineSession,
  CoachingVisionBoardPatch,
  CoachingWinCreate,
  CoachingWeeklyReview,
  ActivationTerms,
  CashoutWindow,
  CourseCatalogQuery,
  CoursePlayer,
  CourseSummary,
  GenealogyNode,
  GenealogyResponse,
  KitCode,
  LedgerEntry,
  MyCreatorSubscription,
  MyTopup,
  PublicCourse,
  WalletTopupBracket,
  RedeemPreview,
  RedeemResult,
  PayoutMethodOption,
  PayoutPreview,
  PayoutRequest,
  LessonSource,
  MyCourse,
  OrderListRow,
  Slot,
  SlotWallet,
  WalletTotals,
  StorefrontContext,
  StorefrontProduct,
  StorefrontResponse,
  StreakState,
  SuccessScore,
} from '@mlm/shared';
import type { CoachPromptCategory } from '@mlm/shared/coaching';
// Re-exported so pages can take these from '../lib/api' alongside the calls
// that return them, rather than reaching into @mlm/shared for the shape and
// here for the fetch.
export type {
  ActivationTerms,
  CashoutWindow,
  CourseCatalogQuery,
  CoursePlayer,
  CourseSummary,
  GenealogyNode,
  GenealogyResponse,
  KitCode,
  LedgerEntry,
  LessonSource,
  MyCreatorSubscription,
  MyTopup,
  PublicCourse,
  WalletTopupBracket,
  MyCourse,
  OrderListRow,
  PayoutMethodOption,
  PayoutPreview,
  PayoutRequest,
  RedeemPreview,
  RedeemResult,
  Slot,
  SlotWallet,
  StorefrontContext,
  StorefrontProduct,
  WalletTotals,
};
import { useAuthStore, type AuthUser } from './auth-store';
import { queryClient } from './query-client';

/**
 * The coach app talks only to the mlm-remake API. It signs in on the MEMBER
 * origin slug, so the refresh cookie and the session are the same ones the
 * SuccessMall store uses: a member signed in there is signed in here.
 */
export const BASE_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';
export const MEMBER_URL: string = import.meta.env.VITE_MEMBER_URL ?? 'http://localhost:5174';
/**
 * Where THIS app is published, for links a member sends out (the invite link).
 * Baked at build time because the phone app's origin is `app.<domain>`, which
 * is not a website (capacitor.config.ts); the web build falls back to its own
 * origin, which is the right answer there.
 */
export const COACH_URL: string =
  import.meta.env.VITE_COACH_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

export const api: AxiosInstance = axios.create({ baseURL: BASE_URL, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function apiErrorPayload(e: unknown): { error?: string; [k: string]: unknown } | undefined {
  return axios.isAxiosError(e) ? (e.response?.data as { error?: string } | undefined) : undefined;
}

export function isRateLimited(e: unknown): boolean {
  return axios.isAxiosError(e) && e.response?.status === 429;
}

const ERROR_COPY: Record<string, string> = {
  birth_details_required: 'Add your birth date and place in Settings to see this.',
  birth_time_required: 'Enter your birth time, or mark it as unknown.',
  birth_coordinates_incomplete: 'Choose your birth place from the search results.',
  birth_date_in_future: 'Check your birth date.',
  birth_date_out_of_range: 'Check your birth date.',
  primary_goal_required: 'Set your primary goal first.',
  goal_limit_reached: 'You have reached the limit for active goals.',
  milestone_limit_reached: 'This goal has as many milestones as it can hold.',
  action_limit_reached: 'That day already has as many actions as it can hold.',
  primary_goal_must_be_active: 'Only an active goal can be your primary goal.',
  geocoder_unavailable: 'Place search is unavailable right now. Try again shortly.',
  goal_not_found: 'That goal no longer exists.',
  action_not_found: 'That action no longer exists.',
  milestone_not_found: 'That milestone no longer exists.',
  email_taken: 'That email is already registered. Try logging in.',
  unknown_sponsor_code: 'That sponsor code is not recognized.',
  signup_requires_referral_link:
    'Registration is by invitation. Please open the link your sponsor shared.',
  signup_requires_sponsor: 'A sponsor code is required to sign up.',
  terms_not_accepted: 'Please accept the Terms and Privacy Policy to continue.',
  terms_version_mismatch: 'The Terms were just updated. Reload the page and try again.',
  username_taken: 'That username is taken. Please choose another.',
  username_required: 'Please choose a username to continue.',
  registration_field_required: 'Some registration details are missing.',
  evening_review_done: 'You have already closed today with an evening review.',
  generation_unavailable: 'Master Kala could not write that right now. Please try again shortly.',
  affirmation_limit_reached: 'Your list is full. Remove an affirmation to add another.',
  journal_limit_reached: 'You have written as many journal entries today as a day can hold.',
  journal_entry_not_found: 'That journal entry no longer exists.',
  affirmation_not_found: 'That affirmation no longer exists.',
  // Phase 3. `coaching_pro_required` is handled by ProLock wherever a screen
  // can show the upgrade in place; this is the fallback when one slips past.
  coaching_pro_required: 'This is part of a higher plan. You can upgrade from your profile.',
  coaching_plan_required:
    'Conversations with Master Kala are part of a higher plan. You can upgrade from your profile.',
  // The paywall is the real answer to this; the copy covers a member who
  // never subscribed as well as one whose month ended.
  coaching_subscription_required:
    'Miracles Protocol needs an active subscription for this. Subscribe or renew to continue.',
  credits_exhausted: 'You have used your credits for now. Get more from your profile to continue.',
  coaching_goal_limit: 'A paid plan lets you work on more than one goal at a time.',
  coaching_plan_unavailable: 'That plan is not on sale right now.',
  coaching_plan_qty_must_be_one: 'You can buy one plan at a time.',
  coaching_plan_requires_account: 'Please sign in before buying a plan.',
  unknown_payment_method: 'Choose a payment method to continue.',
  payment_method_not_allowed_for_virtual: 'That payment method cannot be used for a plan.',
  slot_required_for_non_cash: 'Your wallet is not available here. Choose another payment method.',
  order_not_payable: 'That payment is already settled.',
  gateway_initiate_failed: 'The payment page could not be opened. Please try again shortly.',
  gateway_payments_disabled: 'Online payment is unavailable right now. Try another method.',
  gateway_not_configured: 'Online payment is unavailable right now. Try another method.',
  order_failed: 'We could not start that payment. Please try again.',
  // Phase 4 tools.
  too_many_lines: 'That session holds fewer lines.',
  three_six_nine_locked:
    'Today’s affirmation is set once a session is done. Keep writing the same one.',
  vision_board_full: 'Your vision board is full. Remove a picture to add another.',
  vision_board_order_mismatch: 'Your board changed. Refresh the page and try again.',
  vision_board_item_not_found: 'That picture is no longer on your board.',
  missing_file: 'Choose a photo first.',
  unsupported_file_type: 'Choose a PNG or JPEG photo.',
  file_too_large: 'That photo is too large. Try a smaller one.',
  file_infected: 'That file could not be accepted.',
  storage_unavailable: 'Uploads are unavailable right now. Please try again shortly.',
  frequency_not_found: 'That track is no longer available.',
  weekly_review_done: 'You have already reviewed that week.',
  invalid_week_start: 'That week is not available for review.',
};

export function apiErrorMessage(e: unknown, fallback?: string): string {
  if (isRateLimited(e)) return 'Too many attempts. Please wait a few minutes and try again.';
  const data = apiErrorPayload(e) as { error?: string; issues?: unknown[] } | undefined;
  if (data?.error && ERROR_COPY[data.error]) return ERROR_COPY[data.error]!;
  if (data?.error === 'validation_error') return 'Please check the details you entered.';
  const status = axios.isAxiosError(e) ? e.response?.status : undefined;
  if (status !== undefined && status >= 500)
    return 'Something went wrong on our side. Please try again in a moment.';
  if (status !== undefined) return fallback ?? 'That did not work. Please try again.';
  // No response at all: the request never reached the server.
  return fallback ?? "We couldn't connect right now. Check your internet connection and try again.";
}

let refreshInFlight: Promise<string | null> | null = null;
async function refreshTokenOnce(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await axios.post<{ accessToken: string }>(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        return res.data.accessToken;
      } catch {
        return null;
      } finally {
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const url = String(original?.url ?? '');
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !url.includes('/auth/refresh') &&
      !url.includes('/auth/login') &&
      !url.includes('/me/password')
    ) {
      original._retried = true;
      const token = await refreshTokenOnce();
      if (token) {
        const user = useAuthStore.getState().user;
        if (user) useAuthStore.getState().setSession(token, user);
        original.headers.Authorization = `Bearer ${token}`;
        return api.request(original);
      }
      useAuthStore.getState().clear();
      queryClient.clear();
    }
    return Promise.reject(error);
  },
);

type MeResponse = {
  id: string;
  email: string;
  fullName: string | null;
  roles: string[];
  perms: string[];
  totpEnabled: boolean;
  mustChangePassword?: boolean;
  impersonator?: { userId: string; email: string } | null;
};

const toUser = (u: MeResponse): AuthUser => ({
  id: u.id,
  email: u.email,
  fullName: u.fullName,
  roles: u.roles,
  perms: u.perms,
  totpEnabled: u.totpEnabled,
  mustChangePassword: u.mustChangePassword ?? false,
  impersonator: u.impersonator ?? null,
});

export async function login(args: {
  identifier: string;
  password: string;
  totp?: string;
}): Promise<AuthUser> {
  const res = await api.post<{ accessToken: string; user: MeResponse }>('/auth/login', args);
  const user = toUser(res.data.user);
  useAuthStore.getState().setSession(res.data.accessToken, user);
  return user;
}

export async function restoreSession(): Promise<boolean> {
  const token = await refreshTokenOnce();
  if (!token) return false;
  try {
    const res = await api.get<MeResponse>('/me', { headers: { Authorization: `Bearer ${token}` } });
    useAuthStore.getState().setSession(token, toUser(res.data));
    return true;
  } catch {
    return false;
  }
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    /* ignore */
  }
  useAuthStore.getState().clear();
  queryClient.clear();
}

export async function exchangeImpersonationToken(token: string): Promise<AuthUser> {
  const res = await api.post<{ accessToken: string; user: MeResponse }>(
    '/auth/impersonation-exchange',
    { token },
  );
  const user = toUser(res.data.user);
  useAuthStore.getState().setSession(res.data.accessToken, user);
  return user;
}

export async function endImpersonation(): Promise<void> {
  try {
    await api.post('/auth/end-impersonation');
  } catch {
    /* best effort */
  }
}

export async function changePassword(args: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  await api.post('/me/password', args);
}

// ── public config + signup ───────────────────────────────────────────────

export type PublicConfig = {
  coachingEnabled: boolean;
  tenantName: string;
  signupMode: string;
  /** The tenant requires extra registration fields this app does not collect. */
  needsFullRegistration: boolean;
  /** Whether signup asks for a birth date (cashier phase 5). */
  birthDateField: 'hidden' | 'optional' | 'required';
  /** A visitor who is not signed in may browse the courses (the server enforces it too). */
  lmsPublicCatalog: boolean;
  /** Phone app: a course the member cannot open links to the same course on the website. */
  lmsAppWebLink: boolean;
  /** Under invite-only signup, a campaign page or a course being joined counts as the invite. */
  campaignPageSignupAllowed: boolean;
};

export async function getPublicConfig(): Promise<PublicConfig> {
  const res = await api.get<{
    coachingEnabled?: boolean;
    name?: string;
    signupMode?: string;
    registrationFields?: Array<{ required?: boolean }>;
    birthDateField?: 'hidden' | 'optional' | 'required';
    lmsPublicCatalog?: boolean;
    lmsAppWebLink?: boolean;
    campaignPageSignupAllowed?: boolean;
  }>('/tenant/config');
  return {
    coachingEnabled: res.data.coachingEnabled === true,
    tenantName: res.data.name ?? '',
    signupMode: res.data.signupMode ?? 'on',
    needsFullRegistration: (res.data.registrationFields ?? []).some((f) => f.required),
    birthDateField: res.data.birthDateField ?? 'hidden',
    lmsPublicCatalog: res.data.lmsPublicCatalog === true,
    lmsAppWebLink: res.data.lmsAppWebLink === true,
    campaignPageSignupAllowed: res.data.campaignPageSignupAllowed === true,
  };
}

export type SignupTerms = {
  termsText: string;
  termsVersion: number;
  privacyText: string;
  privacyVersion: number;
  requireOnSignup: boolean;
  requireUsernameOnSignup: boolean;
};

export async function getSignupTerms(): Promise<SignupTerms> {
  return (await api.get<SignupTerms>('/auth/terms')).data;
}

export async function signup(args: {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  username?: string;
  sponsorCode?: string;
  termsAccepted?: boolean;
  termsVersion?: number;
  /** `YYYY-MM-DD`, when the tenant asks for it. */
  birthDate?: string;
  /** The course a visitor is joining to buy: counts as the invite where the tenant allows it. */
  joinCourseProductId?: string;
}): Promise<void> {
  await api.post('/auth/signup', args);
}

// ── coaching ─────────────────────────────────────────────────────────────

export type CoachingProfile = {
  firstName: string | null;
  birthDate: string | null;
  birthTime: string | null;
  birthTimeKnown: boolean;
  birthPlace: string | null;
  birthLat: number | null;
  birthLng: number | null;
  birthTimezone: string | null;
  timezone: string;
  focusCategory: string | null;
  assessment: { satisfaction?: number; confidence?: number; blockers?: string[] };
  ritualPrefs: { minutes?: number; modules?: string[] };
  coachingStyle: string | null;
  coachingNeeds: string[];
  notificationPrefs: {
    morningReminder?: string | null;
    eveningReminder?: string | null;
    coachNudges?: boolean;
  };
  onboardingCompletedAt: string | null;
  hasBirthDetails: boolean;
};

export type Milestone = {
  id: string;
  goalId: string;
  title: string;
  targetDate: string | null;
  status: 'pending' | 'done';
  completedAt: string | null;
  orderIndex: number;
};

export type Goal = {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  targetDate: string | null;
  reasonWhy: string | null;
  status: 'active' | 'achieved' | 'archived';
  isPrimary: boolean;
  progressPercent: number;
  achievedAt: string | null;
  createdAt: string;
  milestones: Milestone[];
};

export type Action = {
  id: string;
  goalId: string | null;
  source: 'ai' | 'user' | 'system';
  title: string;
  notes: string | null;
  priority: number;
  estimatedMinutes: number | null;
  scheduledDate: string;
  status: 'pending' | 'done' | 'partial' | 'skipped';
  completedAt: string | null;
  rescheduledFrom: string | null;
  createdAt: string;
};

export type Today = {
  day: string;
  profile: {
    firstName: string | null;
    onboardingCompleted: boolean;
    hasBirthDetails: boolean;
    focusCategory: string | null;
    coachingStyle: string | null;
  };
  score: SuccessScore;
  streak: StreakState;
  actions: Action[];
  primaryGoal: Goal | null;
  nextMilestone: Milestone | null;
  affirmation: string;
  insight: string;
  copySource: string;
  /** Practice kinds recorded today, such as 'affirmation' or 'journal'. */
  practicesDone: string[];
  /** Tonight's journal question from the daily engine (null before onboarding finishes). */
  reflectionPrompt: string | null;
  ritualRecommendation: { module: string; minutes: number; note: string } | null;
  ritual: {
    morning: {
      started: boolean;
      completed: boolean;
      stepsCompleted: number;
      stepsTotal: number;
      intention: string | null;
    };
    evening: { completed: boolean; summary: string | null; tomorrowPriority: string | null };
  };
  alignment: AlignmentSnapshot | null;
  disclaimer: string | null;
};

export type ScoreResponse = {
  day: string;
  score: SuccessScore;
  streak: StreakState;
  history: Array<{ day: string; score: number }>;
};

export type MoodDay = { day: string; mood: number | null; feeling: CoachingFeeling | null };

export type MoodResponse = {
  day: string;
  days: MoodDay[];
  average: number | null;
  recorded: number;
  commonFeeling: CoachingFeeling | null;
};

export type MiracleWin = {
  id: string;
  day: string;
  note: string;
  amount: string | null;
  createdAt: string;
};

export type Planet = {
  name: string;
  sign: string;
  house: number;
  longitude: number;
  retrograde: boolean;
};
export type DashaSpan = { lord: string; start: string; end: string };

export type ChartResponse = {
  chart: {
    provider: 'local' | 'prokerala';
    fallback: boolean;
    ascendant: { sign: string; longitude: number } | null;
    planets: Planet[];
    houses: Array<{ number: number; sign: string; planets: string[] }>;
    moonNakshatra: { name: string; index: number; rulingPlanet: string; pada: number };
    warnings: string[];
  };
  cached: boolean;
  currentDasha: { maha: DashaSpan; antar: DashaSpan; pratyantar: DashaSpan } | null;
  readings: {
    lagna: string | null;
    secondHouse: string | null;
    eleventhHouse: string | null;
    yogas: Array<{ name: string; present: boolean; explanation: string }>;
    nakshatra: string;
    dasha: string | null;
  };
  disclaimer: string;
};

export type DailyGuidance = {
  day: string;
  headline: string;
  summary: string;
  bestFor: string[];
  careWith: string[];
  alignment: AlignmentSnapshot;
  reference: 'ascendant' | 'moon';
  moon: { sign: string; nakshatra: string; natalHouse: number };
  transits: Array<{ planet: string; sign: string; natalHouse: number; retrograde: boolean }>;
  backdrop: Array<{ planet: 'Jupiter' | 'Saturn'; natalHouse: number; note: string }>;
  dasha: { maha: string; antar: string; pratyantar: string; antarEnds: string } | null;
  source: 'ai' | 'rules';
  disclaimer: string;
};

export type PlaceCandidate = {
  displayName: string;
  lat: number;
  lng: number;
  timezone: string | null;
};

export type JournalEntry = {
  id: string;
  kind: 'free' | 'prompt' | 'gratitude' | 'evening';
  day: string;
  prompt: string | null;
  content: string;
  mood: number | null;
  tags: string[];
  goalId: string | null;
  aiReflection: string | null;
  aiReflectedAt: string | null;
  createdAt: string;
};

export type Affirmation = {
  id: string;
  text: string;
  source: 'ai' | 'user' | 'library';
  favorite: boolean;
  inRitual: boolean;
  createdAt: string;
};

export type EveningResult = {
  day: string;
  summary: string;
  insight: string;
  tomorrowPriority: string | null;
  source: 'ai' | 'rules';
  score: SuccessScore;
  streak: StreakState;
  tomorrowAction: Action | null;
};

export type ActionSuggestion = { title: string; estimatedMinutes: number };

// ── Phase 4: Align and Manifest tools ────────────────────────────────────────

export type BreathworkPattern = {
  /** A built-in key (`box`) or an admin row's id. */
  id: string;
  title: string;
  description: string;
  outcome: string;
  pattern: { inhale: number; holdIn: number; exhale: number; holdOut: number };
  cycles: number;
  totalSeconds: number;
  premiumLevel: CoachingTier;
  source: 'builtin' | 'admin';
};

export type Mantra = {
  /** A built-in key (`om`) or an admin row's id. */
  id: string;
  title: string;
  script: string;
  meaning: string;
  use: string;
  intention: string;
  repetitions: number;
  premiumLevel: CoachingTier;
  source: 'builtin' | 'admin';
};

export type MantraLibrary = {
  mantras: Mantra[];
  intentions: string[];
  counts: number[];
  locked: boolean;
  proOnlyCount: number;
};

export type Meditation = {
  id: string;
  title: string;
  description: string;
  intention: string;
  steps: Array<{ text: string; seconds: number }>;
  minutes: number;
  premiumLevel: CoachingTier;
  source: 'builtin' | 'admin';
};

export type MeditationLibrary = {
  meditations: Meditation[];
  intentions: string[];
  locked: boolean;
  proOnlyCount: number;
};

export type FutureSelf = {
  horizon: FutureSelfHorizon;
  answers: Partial<Record<FutureSelfPromptKey, string>>;
  progress: number;
  updatedAt: string;
};

export type AlignmentTodo = { id: string; text: string; done: boolean };

export type PalmReading = {
  readable: boolean;
  handType: string;
  overview: string;
  lines: { life: string; head: string; heart: string; fate: string };
  mounts: string;
  markings: string;
  guidance: string;
  disclaimer: string;
};

export type AuraColorReading = {
  key: string;
  name: string;
  hex: string;
  theme: string;
  strength: string;
  score: number;
};

export type AuraResponse = {
  colors: { primary: AuraColorReading; secondary: AuraColorReading; supporting: AuraColorReading };
  strength: number;
  chakras: Record<string, number>;
  dimensions: Record<string, number>;
  scores: Record<string, number>;
  element: string;
  reference: 'ascendant' | 'moon';
  practiceSessions: number;
  overview: string;
  suggestion: string;
  source: 'ai' | 'rules';
  disclaimer: string;
};

export type KarmaReading = {
  dasha: { maha: string; antar: string } | null;
  referenceLord: string;
  reference: 'ascendant' | 'moon';
  pattern: string;
  ketu: { house: number; sign: string };
  path: string;
  rahu: { house: number; sign: string };
  patience: string;
  saturn: { house: number };
  lesson: string;
  cleansing: { name: string; why: string; steps: string[] };
  lucky: {
    numbers: number[];
    day: string;
    gem: string;
    colors: Array<{ name: string; hex: string }>;
  };
  source: 'ai' | 'rules';
  disclaimer: string;
};

export type ManifestationPractice = {
  id: string;
  title: string;
  origin: string;
  description: string;
  steps: Array<{ text: string; seconds: number }>;
  minutes: number;
  premiumLevel: CoachingTier;
  source: 'builtin' | 'admin';
};

export type BreathworkLibrary = {
  patterns: BreathworkPattern[];
  outcomes: string[];
  /** The tenant made the tools Pro-only and this member is free. */
  locked: boolean;
  /** How many more patterns Pro would open. */
  proOnlyCount: number;
};

export type FrequencyTrack = {
  id: string;
  title: string;
  description: string;
  /** A key from the library's `categories`. */
  category: string;
  frequencyLabel: string | null;
  durationSeconds: number | null;
  premiumLevel: CoachingTier;
  /** `upload` streams from our bucket; `link` plays `embed` straight from its host. Absent on an older API build. */
  source?: 'upload' | 'link';
  embed?: { kind: 'iframe' | 'video'; src: string } | null;
};

/** A listening intention: the tenant's own list, or the built-ins. */
export type FrequencyCategory = { key: string; label: string; hue: number | null };

/** One item of the content library, as a member sees it. */
export type LibraryItem = {
  id: string;
  kind: 'video' | 'audio' | 'image' | 'document';
  title: string;
  description: string;
  category: string | null;
  source: 'upload' | 'link';
  /** A linked item's playable form, or its address; null for an upload (opened through `open`). */
  embed: { kind: 'iframe' | 'video' | 'link'; src: string } | null;
  durationSeconds: number | null;
  premiumLevel: CoachingTier;
  /** The member's plan is below the item's level: listed, not openable. */
  locked: boolean;
};
export type LibraryList = {
  items: LibraryItem[];
  categories: Array<{ key: string; label: string }>;
  locked: boolean;
  off: boolean;
};
export type LibraryQuery = { q?: string; kind?: LibraryItem['kind']; category?: string };

export type FrequencyLibrary = {
  tracks: FrequencyTrack[];
  categories: FrequencyCategory[];
  locked: boolean;
  proOnlyCount: number;
};

export type ThreeSixNineEntry = {
  day: string;
  affirmation: string;
  sessions: Record<CoachingThreeSixNineSession, { lines: string[]; doneAt: string | null }>;
  complete: boolean;
};

export type ThreeSixNineState = {
  day: string;
  entry: ThreeSixNineEntry | null;
  streak: number;
  counts: Record<CoachingThreeSixNineSession, number>;
};

export type VisionBoardItem = {
  id: string;
  imageUrl: string;
  caption: string | null;
  goalId: string | null;
  goalTitle: string | null;
  sortOrder: number;
  createdAt: string;
};

export type WeeklyContext = {
  weekStart: string;
  weekEnd: string;
  actions: { done: number; total: number };
  averageScore: number | null;
  bestDay: { day: string; score: number } | null;
  morningRituals: number;
  eveningReviews: number;
  threeSixNineDays: number;
  streak: number;
  wins: string[];
};

export type WeeklyReview = {
  weekStart: string;
  answers: Record<string, string>;
  summary: { wins: string; pattern: string; focus: string; topPriorities: string[] };
  source: 'ai' | 'template';
  completedAt: string | null;
};

export type WeeklyReviewState = {
  weekStart: string;
  currentWeekStart: string;
  review: WeeklyReview | null;
  context: WeeklyContext;
};

const data = <T>(p: Promise<{ data: T }>) => p.then((r) => r.data);

export const coaching = {
  profile: () => data(api.get<{ profile: CoachingProfile; today: string }>('/coaching/profile')),
  updateProfile: (patch: CoachingProfilePatch) =>
    data(api.put<{ profile: CoachingProfile; today: string }>('/coaching/profile', patch)),
  geocode: (query: string) =>
    data(api.post<{ places: PlaceCandidate[] }>('/coaching/geocode', { query })).then(
      (d) => d.places,
    ),
  completeOnboarding: () =>
    data(
      api.post<{ alreadyCompleted: boolean; source: 'ai' | 'rules' | null }>(
        '/coaching/onboarding/complete',
      ),
    ),
  today: () => data(api.get<Today>('/coaching/today')),
  score: () => data(api.get<ScoreResponse>('/coaching/score')),
  mood: (days: number) => data(api.get<MoodResponse>(`/coaching/mood?days=${days}`)),
  wins: (params: { day?: string; days?: number } = {}) =>
    data(
      api.get<{ today: string; wins: MiracleWin[] }>('/coaching/wins', {
        params,
      }),
    ),
  createWin: (input: CoachingWinCreate) =>
    data(api.post<{ win: MiracleWin }>('/coaching/wins', input)),
  deleteWin: (id: string) => api.delete(`/coaching/wins/${id}`).then(() => undefined),
  practice: (practice: CoachingPractice) =>
    data(api.post<{ day: string; score: SuccessScore }>('/coaching/practices', { practice })),
  /** Fire-and-forget analytics; never surfaces an error. */
  event: (name: CoachingEventName, props?: Record<string, string | number | boolean | null>) => {
    void api.post('/coaching/events', { name, props }).catch(() => undefined);
  },
  entitlement: () => data(api.get<Entitlement>('/coaching/entitlement')),
  creditEntries: () =>
    data(api.get<{ entries: CreditEntry[] }>('/coaching/credits/entries')).then((d) => d.entries),
  plans: () => data(api.get<{ plans: CoachingPlan[] }>('/coaching/plans')).then((d) => d.plans),
  chart: () => data(api.get<ChartResponse>('/coaching/chart')),
  guidance: () =>
    data(api.get<{ guidance: DailyGuidance; cached: boolean }>('/coaching/guidance/today')).then(
      (d) => d.guidance,
    ),
  goals: (status: 'active' | 'all' = 'active') =>
    data(api.get<{ goals: Goal[] }>('/coaching/goals', { params: { status } })).then(
      (d) => d.goals,
    ),
  goal: (id: string) => data(api.get<{ goal: Goal }>(`/coaching/goals/${id}`)).then((d) => d.goal),
  createGoal: (input: CoachingGoalCreate) =>
    data(api.post<{ goal: Goal }>('/coaching/goals', input)).then((d) => d.goal),
  updateGoal: (id: string, patch: CoachingGoalUpdate) =>
    data(api.patch<{ goal: Goal }>(`/coaching/goals/${id}`, patch)).then((d) => d.goal),
  addMilestone: (goalId: string, input: CoachingMilestoneCreate) =>
    data(api.post<{ milestone: Milestone }>(`/coaching/goals/${goalId}/milestones`, input)).then(
      (d) => d.milestone,
    ),
  updateMilestone: (id: string, patch: CoachingMilestoneUpdate) =>
    data(api.patch<{ milestone: Milestone }>(`/coaching/milestones/${id}`, patch)).then(
      (d) => d.milestone,
    ),
  deleteMilestone: (id: string) => api.delete(`/coaching/milestones/${id}`).then(() => undefined),
  actions: (range: { day?: string; from?: string; to?: string } = {}) =>
    data(api.get<{ today: string; actions: Action[] }>('/coaching/actions', { params: range })),
  createAction: (input: CoachingActionCreate) =>
    data(api.post<{ action: Action }>('/coaching/actions', input)).then((d) => d.action),
  updateAction: (id: string, patch: CoachingActionUpdate) =>
    data(api.patch<{ action: Action }>(`/coaching/actions/${id}`, patch)).then((d) => d.action),
  deleteAction: (id: string) => api.delete(`/coaching/actions/${id}`).then(() => undefined),
  suggestActions: (input: { goalId: string; day?: string }) =>
    data(
      api.post<{ suggestions: ActionSuggestion[]; source: 'ai' | 'rules' }>(
        '/coaching/actions/suggest',
        input,
      ),
    ),
  morningRitual: (input: CoachingMorningRitual) =>
    data(
      api.post<{ day: string; score: SuccessScore; justCompleted: boolean }>(
        '/coaching/rituals/morning',
        input,
      ),
    ),
  eveningReview: (input: CoachingEveningReview) =>
    data(api.post<EveningResult>('/coaching/rituals/evening', input)),
  journal: (params: { limit?: number; before?: string } = {}) =>
    data(api.get<{ entries: JournalEntry[] }>('/coaching/journal', { params })).then(
      (d) => d.entries,
    ),
  journalPrompt: () =>
    data(
      api.get<{ day: string; prompt: string; alternatives: string[] }>('/coaching/journal/prompt'),
    ),
  createJournalEntry: (input: CoachingJournalCreate) =>
    data(api.post<{ entry: JournalEntry; score: SuccessScore }>('/coaching/journal', input)),
  reflectJournalEntry: (id: string) =>
    data(api.post<{ entry: JournalEntry }>(`/coaching/journal/${id}/reflect`)).then((d) => d.entry),
  deleteJournalEntry: (id: string) => api.delete(`/coaching/journal/${id}`).then(() => undefined),
  affirmations: () =>
    data(api.get<{ affirmations: Affirmation[] }>('/coaching/affirmations')).then(
      (d) => d.affirmations,
    ),
  createAffirmation: (input: { text: string; inRitual?: boolean }) =>
    data(api.post<{ affirmation: Affirmation }>('/coaching/affirmations', input)).then(
      (d) => d.affirmation,
    ),
  updateAffirmation: (id: string, patch: CoachingAffirmationUpdate) =>
    data(api.patch<{ affirmation: Affirmation }>(`/coaching/affirmations/${id}`, patch)).then(
      (d) => d.affirmation,
    ),
  deleteAffirmation: (id: string) =>
    api.delete(`/coaching/affirmations/${id}`).then(() => undefined),
  generateAffirmations: (input: { goalId?: string; count: number }) =>
    data(
      api.post<{ affirmations: Affirmation[]; source: 'ai' | 'library' }>(
        '/coaching/affirmations/generate',
        input,
      ),
    ),
  breathwork: () => data(api.get<BreathworkLibrary>('/coaching/breathwork')),
  library: (query: LibraryQuery = {}) =>
    data(
      api.get<LibraryList>('/coaching/library', {
        params: Object.fromEntries(Object.entries(query).filter(([, v]) => v)),
      }),
    ),
  libraryItem: (id: string) =>
    data(
      api.get<{ item: LibraryItem; open: { url: string; expiresAt: string } | null }>(
        `/coaching/library/${id}`,
      ),
    ),
  frequencies: () =>
    data(api.get<FrequencyLibrary>('/coaching/frequencies')).then((lib) => ({
      ...lib,
      // An older API build sent the category keys as bare strings.
      categories: (lib.categories as Array<FrequencyCategory | string>).map((c) =>
        typeof c === 'string' ? { key: c, label: c, hue: null } : c,
      ),
    })),
  mantras: () => data(api.get<MantraLibrary>('/coaching/mantras')),
  meditations: () => data(api.get<MeditationLibrary>('/coaching/meditations')),
  karma: () => data(api.get<{ karma: KarmaReading; cached: boolean }>('/coaching/karma')),
  aura: (checkIn: CoachingAuraCheckIn) =>
    data(api.post<{ aura: AuraResponse }>('/coaching/aura', checkIn)),
  futureSelf: () => data(api.get<{ horizons: FutureSelf[] }>('/coaching/future-self')),
  saveFutureSelf: (input: CoachingFutureSelfSave) =>
    data(api.put<{ futureSelf: FutureSelf }>('/coaching/future-self', input)),
  alignmentTodos: () =>
    data(api.get<{ day: string; todos: AlignmentTodo[] }>('/coaching/alignment-todos')),
  setAlignmentTodo: (id: string, done: boolean) =>
    data(api.patch<{ todo: AlignmentTodo }>(`/coaching/alignment-todos/${id}`, { done })),
  manifestation: () =>
    data(
      api.get<{ practices: ManifestationPractice[]; proOnlyCount: number }>(
        '/coaching/manifestation',
      ),
    ),
  prompts: () => data(api.get<CoachPromptLibrary>('/coaching/prompts')),
  programs: () =>
    data(api.get<{ programs: ProgramSummary[] }>('/coaching/programs')).then((d) => d.programs),
  program: (idOrKey: string) =>
    data(
      api.get<{ program: ProgramDetail }>(`/coaching/programs/${encodeURIComponent(idOrKey)}`),
    ).then((d) => d.program),
  enrollProgram: (id: string) =>
    data(api.post<{ program: ProgramDetail }>(`/coaching/programs/${id}/enroll`)).then(
      (d) => d.program,
    ),
  leaveProgram: (id: string) =>
    data(api.post<{ program: ProgramDetail }>(`/coaching/programs/${id}/leave`)).then(
      (d) => d.program,
    ),
  programDay: (id: string, dayNo: number) =>
    data(api.get<{ day: ProgramDay }>(`/coaching/programs/${id}/days/${dayNo}`)).then((d) => d.day),
  completeProgramDay: (id: string, dayNo: number) =>
    data(
      api.post<{ day: ProgramDay; programCompleted: boolean }>(
        `/coaching/programs/${id}/days/${dayNo}/complete`,
      ),
    ),
  programStream: (id: string, dayNo: number) =>
    data(
      api.get<{ url: string; expiresAt: string }>(`/coaching/programs/${id}/days/${dayNo}/stream`),
    ),
  frequencyStream: (id: string) =>
    data(api.get<{ url: string; expiresAt: string }>(`/coaching/frequencies/${id}/stream`)),
  threeSixNine: () => data(api.get<ThreeSixNineState>('/coaching/369')),
  saveThreeSixNine: (input: CoachingThreeSixNineSave) =>
    data(
      api.put<{
        day: string;
        entry: ThreeSixNineEntry;
        justCompleted: CoachingThreeSixNineSession | null;
        score: SuccessScore;
        streak: number;
      }>('/coaching/369', input),
    ),
  visionBoard: () =>
    data(api.get<{ items: VisionBoardItem[]; limit: number }>('/coaching/vision-board')),
  readPalm: (form: FormData) =>
    data(api.post<{ reading: PalmReading }>('/coaching/palmistry', form)),
  addVisionBoardItem: (form: FormData) =>
    data(api.post<{ item: VisionBoardItem; score: SuccessScore }>('/coaching/vision-board', form)),
  updateVisionBoardItem: (id: string, patch: CoachingVisionBoardPatch) =>
    data(api.patch<{ item: VisionBoardItem }>(`/coaching/vision-board/${id}`, patch)).then(
      (d) => d.item,
    ),
  reorderVisionBoard: (ids: string[]) =>
    data(api.put<{ items: VisionBoardItem[] }>('/coaching/vision-board/order', { ids })).then(
      (d) => d.items,
    ),
  deleteVisionBoardItem: (id: string) =>
    api.delete(`/coaching/vision-board/${id}`).then(() => undefined),
  weeklyReview: (weekStart?: string) =>
    data(
      api.get<WeeklyReviewState>('/coaching/review/weekly', {
        params: weekStart ? { weekStart } : {},
      }),
    ),
  completeWeeklyReview: (input: CoachingWeeklyReview) =>
    data(
      api.post<{ weekStart: string; review: WeeklyReview; context: WeeklyContext }>(
        '/coaching/review/weekly',
        input,
      ),
    ),
};

// ── Plans + the store (Phase 3) ─────────────────────────────────────────────

export type CoachingTier = 'free' | 'pro' | 'elite';

/** What this member's plan does with one feature. */
export type CoachPrompt = {
  /** The built-in key, or the admin row's id. */
  id: string;
  category: CoachPromptCategory;
  title: string;
  /** What the composer is filled with; the member edits it before sending. */
  body: string;
  premiumLevel: CoachingTier;
  source: 'builtin' | 'admin';
};

export type CoachPromptLibrary = {
  prompts: CoachPrompt[];
  categories: readonly CoachPromptCategory[];
  /** The tenant keeps the library for a higher plan: show the lock, not the list. */
  locked: boolean;
  proOnlyCount: number;
};

// ── guided programs ────────────────────────────────────────────────────────

export type ProgramEnrollment = {
  id: string;
  status: 'active' | 'completed' | 'left';
  startedOn: string;
  completedOn: string | null;
  /** The due day, clamped to the program. */
  currentDay: number;
  completedDays: number;
};

export type ProgramSummary = {
  id: string;
  key: string | null;
  title: string;
  tagline: string;
  daysCount: number;
  premiumLevel: CoachingTier;
  /** A plan above the member's holds this program. */
  locked: boolean;
  enrollment: ProgramEnrollment | null;
};

export type ProgramDayState = 'done' | 'today' | 'available' | 'locked';

export type ProgramDayRow = {
  dayNo: number;
  title: string;
  state: ProgramDayState;
  unlocksOn: string | null;
  actionTitle: string;
};

export type ProgramDetail = ProgramSummary & { description: string; days: ProgramDayRow[] };

export type ProgramDay = {
  program: { id: string; title: string; daysCount: number };
  dayNo: number;
  title: string;
  lesson: string;
  media: { kind: 'audio' | 'video'; ref: string } | null;
  kalaPrompt: string;
  action: {
    title: string;
    minutes: number | null;
    id: string | null;
    status: string | null;
    dueOn: string;
  };
  state: ProgramDayState;
  unlocksOn: string | null;
  lessonDoneAt: string | null;
  next: { dayNo: number; title: string } | null;
};

export type FeatureState = 'on' | 'locked' | 'off';

export type Entitlement = {
  tier: CoachingTier;
  endsAt: string | null;
  source: 'purchase' | 'admin_grant' | null;
  limits: { dailyMessages: number | null; activeGoals: number | null };
  proDailyMessages: number | null;
  /**
   * Per feature, what THIS member may do: use it, see it locked, or not be
   * shown it at all. Read this through `useFeature`, never the two booleans
   * below, which are only kept so an older installed build keeps working.
   */
  features: Partial<Record<CoachingAccessKey, FeatureState>>;
  /** @deprecated the tenant can now split this group; read `features`. */
  chartGuidanceProOnly: boolean;
  /** @deprecated the tenant can now split this group; read `features`. */
  toolsProOnly: boolean;
  freeze: { available: boolean; usedOn: string | null };
  /** The tenant's community link, when one is set: a card on Home and Profile. */
  community: { url: string; label: string | null } | null;
  /**
   * AI Credits: what the member can spend, split into this month's included
   * bundle and credits they bought or earned, what each action costs, and the
   * packs on sale. Absent on an older API build.
   */
  credits?: CreditsView;
  /** Subscription required to use the app (no free tier). Absent on an older API build. */
  paidOnly?: boolean;
  /** The lowest plan that opens each feature, so a lock can name the plan to buy. Absent on an older API build. */
  featureTiers?: Partial<Record<CoachingAccessKey, CoachingTier | 'off'>>;
  /** What the tiers are called here. Absent on an older API build. */
  tierLabels?: Record<CoachingTier, string>;
};

export type CreditsView = {
  total: number;
  included: number;
  includedUntil: string | null;
  purchased: number;
  lifetimeSpent: number;
  costs: Array<{ key: string; label: string; what: string; cost: number }>;
  packs: Array<{ productId: string; name: string; credits: number; price: string }>;
};

export type CreditEntry = {
  id: string;
  direction: 'C' | 'D';
  amount: number;
  kind: string;
  bucket: string;
  action: string | null;
  createdAt: string;
};

/** GET /me/referral-code: the member's own code and the value an invite link should carry. */
export type ReferralCode = {
  code: string | null;
  /** What a link embeds: the encrypted token when the tenant encrypts refs, else the code. */
  shareRef: string | null;
  enabled: boolean;
  canChange: boolean;
};

export const getReferralCode = () => data(api.get<ReferralCode>('/me/referral-code'));

export type CoachingPlan = {
  id: string;
  tier: 'pro' | 'elite';
  period: 'monthly' | 'annual';
  /** "{tier name} · Monthly", with the tier named as the tenant named it. */
  label: string;
  /** The tier's name on its own. Absent on an older API build. */
  tierLabel?: string;
  productId: string;
  name: string;
  price: string;
  /** What this plan adds over the one below it, from the tenant's feature table. Absent on an older API build. */
  included?: string[];
  includedCredits?: number;
};

/**
 * A product as the anonymous feed (`GET /products`) returns it, narrowed to
 * what a visitor's course tile shows. `price` is the public price: a member's
 * own price comes from the signed-in feed.
 */
export type PublicCatalogProduct = {
  id: string;
  name: string;
  images: string[];
  price: string;
};

/** A payment method as the picker needs it. `balance` is null with no slot. */
export type StorePaymentMethod = {
  code: string;
  label: string;
  currency: 'cash' | 'gc' | 'coin';
  settlement: 'wallet' | 'manual' | 'gateway' | 'cod_manual';
  accountLabel: string | null;
  instructions: string | null;
  gatewayProvider: string | null;
  virtualEnabled: boolean;
  balance: string | null;
  /** Paying a creator plan with this method starts a card subscription. Absent on an older API. */
  renewsPlans?: boolean;
};

/** The gateway's word on the latest online attempt (`gateway_transactions.status`). */
export type GatewayAttemptStatus =
  | 'initiated'
  | 'success'
  | 'fail'
  | 'pending'
  | 'void'
  | 'unknown';

export type StoreOrder = {
  id: string;
  code: string;
  status: string;
  total: string;
  createdAt: string;
  paymentMethodCode?: string | null;
  paymentMethodLabel?: string | null;
  /** From the live registry; null when the method was removed after checkout. */
  paymentMethodSettlement?: StorePaymentMethod['settlement'] | null;
  /**
   * The newest online attempt, only on the single-order read. A failed attempt
   * leaves the order payable, so this is the only place a failure shows.
   */
  gatewayPayment?: { provider: string; status: GatewayAttemptStatus; updatedAt: string } | null;
  /**
   * What paying this order opens, only on the single-order read: a coaching
   * plan, courses, a creator's subscription, or none of those (goods). Absent
   * on an older API, which only ever sold the coaching plan here.
   */
  unlocks?: {
    coachingPlan: boolean;
    courseIds: string[];
    subscriptionCreatorIds: string[];
    /** AI Credits the order's packs grant on completion. Absent on an older API build. */
    credits?: number;
  };
};

export type GatewayHandoff =
  | { kind: 'redirect'; url: string }
  | { kind: 'qr'; pollUrl: string }
  | { kind: 'form_post'; actionUrl: string; fields: Record<string, string> };

/**
 * Buying a plan runs through the platform's ordinary checkout, so everything
 * it already does (payments, refunds, receipts) applies unchanged. A coach
 * member has no slot, so the wallet is never an option for them — the picker
 * simply never offers a method whose balance came back null.
 */
/** Public catalog image URL — the API 302-redirects to a presigned object URL. */
export function productImageUrl(productId: string, idx: number): string {
  return `${BASE_URL}/products/${productId}/image/${idx}`;
}

export const store = {
  paymentMethods: () =>
    data(
      api.get<{ slotId: string | null; methods: StorePaymentMethod[] }>(
        '/storefront/payment-methods',
      ),
    ),
  createOrder: (args: { productId: string; paymentMethodCode: string }) =>
    data(
      api.post<StoreOrder>('/orders', {
        type: 'ecom',
        items: [{ productId: args.productId, qty: 1 }],
        paymentMethodCode: args.paymentMethodCode,
      }),
    ),
  /**
   * The catalogue. `slotId` is the member's SELECTED slot and must be passed
   * whenever the caller has one: without it the server prices against their
   * EARLIEST active slot, so a member holding slots at two tiers is shown one
   * price and charged another. A coach-only signup has no slot and passes none.
   */
  catalog: (args?: { search?: string; slotId?: string | null; excludeCourses?: boolean }) =>
    data(
      api.get<StorefrontResponse>('/storefront/products', {
        params: {
          // The Shop sells goods and kits and leaves courses to Learn; Learn's
          // Browse reads this same priced feed to decorate its courses, so the
          // exclusion is the caller's choice, never the default (excluding it
          // here once emptied Browse for every signed-in member).
          ...(args?.excludeCourses ? { excludeCourses: '1' } : {}),
          ...(args?.search ? { search: args.search } : {}),
          ...(args?.slotId ? { slotId: args.slotId } : {}),
        },
      }),
    ),
  product: (id: string, slotId?: string | null) =>
    data(
      api.get<{ context: StorefrontContext; product: StorefrontProduct }>(
        `/storefront/products/${id}`,
        { params: slotId ? { slotId } : {} },
      ),
    ),
  myOrders: (args?: { slotId?: string | null; statuses?: string[]; limit?: number }) =>
    data(
      api.get<OrderListRow[]>('/orders', {
        params: {
          ...(args?.slotId ? { slotId: args.slotId } : {}),
          ...(args?.statuses?.length ? { status: args.statuses.join(',') } : {}),
          ...(args?.limit ? { limit: args.limit } : {}),
        },
      }),
    ),
  /** Multi-line checkout. `createOrder` above stays as the single-plan path. */
  checkout: (args: {
    items: Array<{ productId: string; qty: number; variantId?: string | null }>;
    paymentMethodCode: string;
  }) =>
    data(
      api.post<StoreOrder>('/orders', {
        type: 'ecom',
        items: args.items.map((i) => ({
          productId: i.productId,
          qty: i.qty,
          ...(i.variantId ? { variantId: i.variantId } : {}),
        })),
        paymentMethodCode: args.paymentMethodCode,
      }),
    ),
  order: (id: string) => data(api.get<StoreOrder>(`/orders/${id}`)),
  /**
   * Proof of an offline payment for an ORDER. Distinct from a wallet top-up:
   * this settles a specific order, so it carries the order id and the order's
   * own currency. Used to be an outbound link to the member app, which is a
   * dead host on boxes that publish the store on named hosts, and pointless now
   * that checkout itself lives here.
   */
  uploadProof: async (args: {
    orderId: string;
    method: string;
    amount: string;
    currency: string;
    reference?: string;
    file: File;
  }): Promise<{ id: string; status: string; scanStatus: string | null }> => {
    const form = new FormData();
    form.append('orderId', args.orderId);
    form.append('method', args.method);
    form.append('amount', args.amount);
    form.append('currency', args.currency);
    if (args.reference) form.append('reference', args.reference);
    form.append('file', args.file);
    const res = await api.post<{ id: string; status: string; scanStatus: string | null }>(
      '/payments/manual',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  },
  initiateGateway: (orderId: string) =>
    data(api.post<{ handoff: GatewayHandoff }>('/payments/gateway/initiate', { orderId })).then(
      (d) => d.handoff,
    ),
  /**
   * A THIRD-PARTY creator's course or plan: it is that creator's own order,
   * settled by a manual method or the cash wallet only (no online gateway takes
   * a partner's order). A house course goes through `checkout` like anything else.
   */
  creatorCheckout: (args: {
    creatorUserId: string;
    productId: string;
    paymentMethodCode: string;
    buyerSlotId?: string | null;
  }) =>
    data(
      api.post<StoreOrder>('/creator-orders', {
        creatorUserId: args.creatorUserId,
        items: [{ productId: args.productId, qty: 1 }],
        paymentMethodCode: args.paymentMethodCode,
        ...(args.buyerSlotId ? { buyerSlotId: args.buyerSlotId } : {}),
      }),
    ),
};

/**
 * Hand the browser to the gateway. A redirect or QR is a navigation; a
 * form_post has to be a real POST, so it is submitted through a hidden form.
 * The gateway returns the member to this origin at `/orders/:id?pay=…`,
 * because the coach app is registered as a member origin.
 */
export function performGatewayHandoff(handoff: GatewayHandoff): void {
  if (handoff.kind === 'redirect' || handoff.kind === 'qr') {
    window.location.assign(handoff.kind === 'redirect' ? handoff.url : handoff.pollUrl);
    return;
  }
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = handoff.actionUrl;
  form.style.display = 'none';
  for (const [name, value] of Object.entries(handoff.fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

/** Peso amounts, display only: the server owns every number that is charged. */
export function fmtPeso(amount: string | number): string {
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  }).format(n);
}

// ── Learning (Phase 2) ──────────────────────────────────────────────────────
/**
 * The same LMS the member portal teaches from. Everything that decides access,
 * drip and progress is server-side; these are thin calls.
 */
export const learn = {
  myCourses: (cursor?: string | null) =>
    data(
      api.get<{ items: MyCourse[]; nextCursor: string | null }>('/me/courses', {
        params: cursor ? { cursor } : {},
      }),
    ),
  player: (courseId: string) => data(api.get<CoursePlayer>(`/courses/${courseId}/player`)),
  /**
   * `refresh` re-presigns an expiring R2 URL WITHOUT counting as a play: it
   * skips the progress upsert, the daily view bump and the drip re-check, so a
   * mid-lesson TTL swap cannot inflate "views = plays" or 403 a lesson the
   * learner passed the gate for minutes ago.
   */
  openLesson: (courseId: string, lessonId: string, opts: { refresh?: boolean } = {}) =>
    data(
      api.post<LessonSource>(
        `/courses/${courseId}/lessons/${lessonId}/open`,
        opts.refresh ? { refresh: true } : {},
      ),
    ),
  completeLesson: (courseId: string, lessonId: string, via: 'player' | 'manual') =>
    data(api.post(`/courses/${courseId}/lessons/${lessonId}/complete`, { via })),
  /** The course catalogue, filtered and paged server-side (search, access level, sort). */
  catalog: (query: CourseCatalogQuery = {}) =>
    data(
      api.get<{ courses: CourseSummary[]; total: number }>('/storefront/courses', {
        params: {
          ...(query.search ? { search: query.search } : {}),
          ...(query.accessLevel ? { accessLevel: query.accessLevel } : {}),
          ...(query.categoryId ? { categoryId: query.categoryId } : {}),
          ...(query.sort ? { sort: query.sort } : {}),
          ...(query.limit != null ? { limit: query.limit } : {}),
          ...(query.offset != null ? { offset: query.offset } : {}),
        },
      }),
    ),
  /** One course's public page by its PRODUCT id: outline, instructor, how it is paid for. */
  course: (productId: string) => data(api.get<PublicCourse>(`/storefront/courses/${productId}`)),
  /**
   * The anonymous product feed: a VISITOR's names, pictures and prices for the
   * course tiles (a signed-in member reads the priced `/storefront/products`).
   */
  publicProducts: () =>
    data(
      api.get<PublicCatalogProduct[]>('/products', {
        params: { activeOnly: true, pageSize: 200 },
      }),
    ),
  /** One product from the anonymous feed: a visitor's price for a paid course. */
  publicProduct: (productId: string) =>
    data(api.get<PublicCatalogProduct>(`/products/${productId}`)),
  /** Free access, or access the member already holds (a subscription): no order. */
  enroll: (courseId: string) => data(api.post(`/courses/${courseId}/enroll`, {})),
  /** The member's creator subscriptions, one row per creator, lapsed ones included. */
  subscriptions: () => data(api.get<MyCreatorSubscription[]>('/me/subscriptions/creators')),
  /** A one-time Stripe Customer Portal link (card, cancel or resume, invoices). */
  billingPortal: () => data(api.post<{ url: string }>('/me/subscriptions/billing-portal', {})),
};

/**
 * The resume-position write.
 *
 * Deliberately NOT a react-query mutation: the shared MutationCache toasts every
 * mutation failure and this fires every ten seconds during playback, so one
 * flaky moment on mobile data would bury the learner in error toasts about a
 * write they never asked for. Failures are swallowed; the next beat carries the
 * position anyway.
 *
 * `maxPositionSeconds` is monotonic server-side, and an `ended` beat MUST send
 * the high-water mark alongside `positionSeconds: 0`, or writing 0 regresses the
 * mark and locks the learner back at the start of a lesson they just finished.
 */
export async function writeLessonPosition(
  courseId: string,
  lessonId: string,
  positionSeconds: number,
  extra: { maxPositionSeconds?: number; ended?: boolean } = {},
): Promise<{ ok: boolean; status?: number }> {
  try {
    await api.put(`/courses/${courseId}/lessons/${lessonId}/position`, {
      positionSeconds,
      ...extra,
    });
    return { ok: true };
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    return { ok: false, status };
  }
}

// ── Wallet (Phase 3) ────────────────────────────────────────────────────────
/**
 * A member's money, read-only. A coach-only signup has NO slot (slots come only
 * from redeeming a kit), so every one of these tolerates an empty answer and the
 * screen says so rather than showing zeroes as if they were a balance.
 */
export const wallet = {
  slots: () => data(api.get<Slot[]>('/slots')),
  totals: () => data(api.get<WalletTotals>('/wallet/totals')),
  balance: (slotId: string | null) =>
    data(api.get<SlotWallet>('/wallet', { params: slotId ? { slotId } : {} })),
  ledger: (args: { slotId?: string; plan?: string; page?: number; pageSize?: number }) =>
    data(
      api.get<LedgerEntry[]>('/wallet/ledger', {
        params: {
          slotId: args.slotId,
          plan: args.plan,
          page: args.page ?? 1,
          pageSize: args.pageSize ?? 50,
        },
      }),
    ),
};

// ── Cash-out (Phase 3b) ─────────────────────────────────────────────────────
/**
 * Requesting money out. Every rule that decides whether a request is allowed
 * (the window, KYC, a saved QR, per-slot limits, one-at-a-time) is enforced
 * SERVER-side; this client reads the same settings only so it can explain a
 * refusal before the member fills in a form that was never going to be accepted.
 */
export const payouts = {
  window: () => data(api.get<CashoutWindow>('/payouts/window')),
  methods: () => data(api.get<PayoutMethodOption[]>('/payouts/methods')),
  qrs: () => data(api.get<{ items: Array<{ id: string; method: string }> }>('/me/payout-qrs')),
  requests: () => data(api.get<PayoutRequest[]>('/payouts/requests')),
  preview: (args: { slotId: string; amount: string; method: string }) =>
    data(api.post<PayoutPreview>('/payouts/requests/preview', args)),
  create: (args: {
    slotId: string;
    amount: string;
    method: string;
    payload?: Record<string, unknown>;
  }) => data(api.post<PayoutRequest>('/payouts/requests', args)),
  cancel: (id: string) => data(api.post(`/payouts/requests/${id}/cancel`)),
};

// ── Network / genealogy (Phase 4) ───────────────────────────────────────────
/**
 * The tree. Read one level at a time rather than pulling a deep slice: a phone
 * cannot usefully draw a pan-and-zoom tree, so the screen drills down and each
 * step is rooted at the slot being looked at.
 *
 * WHICH binary fields come back is a tenant setting applied server-side, so the
 * client renders what arrived and never assumes a field exists.
 */
export const network = {
  genealogy: (args: { slotId: string; view: 'unilevel' | 'binary'; depth?: number }) =>
    data(
      api.get<GenealogyResponse>(`/slots/${args.slotId}/genealogy`, {
        params: { view: args.view, depth: args.depth ?? 2 },
      }),
    ),
};

// ── Kit activation / the vault (Phase 5) ────────────────────────────────────
/**
 * Code+PIN activation. This is the ONLY member-facing way a slot comes into
 * existence, which is why it has to exist in the app: without it a member can
 * buy a kit and then have nowhere to use the code.
 *
 * `code` and `pin` are plaintext by design (gift-card UX, staff read the PIN
 * back), so any screen showing them is showing a live credential.
 */
export const kits = {
  myCodes: (onlyAvailable?: boolean) =>
    data(api.get<KitCode[]>('/me/codes', { params: onlyAvailable ? { available: '1' } : {} })),
  activationTerms: () => data(api.get<ActivationTerms>('/kits/activation-terms')),
  /** Non-committing dry run: resolves the sponsor so the member can confirm. */
  preview: (args: { code: string; pin: string; sponsorCode?: string }) =>
    data(api.post<RedeemPreview>('/kits/redeem/preview', args)),
  redeem: (args: {
    code: string;
    pin: string;
    sponsorCode?: string;
    anchorSlotId?: string;
    side?: 'L' | 'R';
    termsAccepted?: boolean;
    termsVersion?: number;
  }) => data(api.post<RedeemResult>('/kits/redeem', args)),
};

// ── Wallet top-up (Phase 6) ─────────────────────────────────────────────────
/**
 * Putting money IN. A top-up is a manual payment: the member pays offline and
 * uploads proof, and an admin approves it. Nothing is credited here.
 *
 * The BONUS is never computed on the device. Brackets are shown so a member can
 * see what they would earn, but the server re-derives the bonus from the same
 * brackets at approval, so a stale cached bracket cannot become a stale credit.
 */
export type TopupPolicy = {
  enabled: boolean;
  brackets: WalletTopupBracket[];
  /** An approved top-up to an in-debt CD slot pays the debt down first. */
  settlesCdDebt: boolean;
};

export const topups = {
  policy: async (): Promise<TopupPolicy> => {
    const res = await api.get<{
      policy?: {
        walletTopupEnabled?: boolean;
        walletTopupBrackets?: WalletTopupBracket[];
        cdTopupSettlesDebt?: boolean;
      };
    }>('/me');
    return {
      enabled: res.data.policy?.walletTopupEnabled === true,
      brackets: res.data.policy?.walletTopupBrackets ?? [],
      settlesCdDebt: res.data.policy?.cdTopupSettlesDebt === true,
    };
  },
  mine: () => data(api.get<MyTopup[]>('/wallet/topups')),
  submit: async (args: {
    slotId: string;
    method: string;
    amount: string;
    reference?: string;
    file: File;
  }): Promise<{ id: string; status: string }> => {
    const form = new FormData();
    form.append('slotId', args.slotId);
    form.append('method', args.method);
    form.append('amount', args.amount);
    if (args.reference) form.append('reference', args.reference);
    form.append('file', args.file);
    const res = await api.post<{ id: string; status: string }>('/wallet/topups', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
};
